import React, { useEffect, useMemo, useState } from "react";
import Cleave from "cleave.js/react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  FormText,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  CLIENT_GENDERS,
  Client,
  ClientGender,
  createClient,
} from "../../services/clientService";
import { CLIENT_OPERATIONAL_NOTES_MAX_LENGTH } from "../../helpers/clientNotes";

const DEFAULT_COUNTRY_CODE = "55";

const isBrazilianLocale = (locale: string): boolean => locale === "pt-BR";

export type QuickClientFormValues = {
  name: string;
  country_code: string;
  phone_display: string;
  secondary_phone_display: string;
  gender: ClientGender | "";
  instagram_handle: string;
  acquisition_source: string;
  notes: string | null;
};

type QuickClientCreateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (client: Client) => void;
};

const emptyFormValues = (): QuickClientFormValues => ({
  name: "",
  country_code: DEFAULT_COUNTRY_CODE,
  phone_display: "",
  secondary_phone_display: "",
  gender: "",
  instagram_handle: "",
  acquisition_source: "",
  notes: null,
});

const toNullable = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? "").trim();

  return trimmed === "" ? null : trimmed;
};

const normalizeInstagram = (value: string): string | null => {
  const normalized = value.trim().replace(/^@+/, "").toLowerCase();
  return normalized === "" ? null : normalized;
};

const QuickClientCreateModal = ({
  isOpen,
  onClose,
  onSuccess,
}: QuickClientCreateModalProps) => {
  const { t, i18n } = useTranslation();
  const hideCountryCode = isBrazilianLocale(i18n.language);
  const [apiError, setApiError] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().required(t("clients.validation.nameRequired")),
        country_code: hideCountryCode
          ? Yup.string()
          : Yup.string()
              .trim()
              .required(t("studio.clients.quick_create.validation.country_code_required"))
              .matches(
                /^\d{1,4}$/,
                t("studio.clients.quick_create.validation.country_code_invalid")
              ),
        phone_display: Yup.string()
          .required(t("clients.validation.phoneRequired"))
          .test("phone-digits", t("clients.validation.phoneInvalid"), (value) => {
            const digits = (value || "").replace(/\D/g, "");

            if (hideCountryCode) {
              return digits.length >= 10 && digits.length <= 11;
            }

            return digits.length >= 8 && digits.length <= 15;
          }),
        secondary_phone_display: Yup.string()
          .trim()
          .test("secondary-phone-digits", t("clients.validation.phoneInvalid"), (value) => {
            const digits = (value || "").replace(/\D/g, "");
            if (digits.length === 0) {
              return true;
            }
            if (hideCountryCode) {
              return digits.length >= 10 && digits.length <= 11;
            }
            return digits.length >= 8 && digits.length <= 15;
          }),
        gender: Yup.string()
          .oneOf(["", ...CLIENT_GENDERS], t("clients.validation.genderInvalid"))
          .nullable(),
        instagram_handle: Yup.string()
          .trim()
          .transform((value) => (value === "" ? "" : value))
          .max(64, t("clients.validation.instagramMax"))
          .test("instagram-format", t("clients.validation.instagramInvalid"), (value) => {
            if (!value) {
              return true;
            }
            const normalized = value.replace(/^@+/, "").toLowerCase();
            return /^[a-z0-9._]{1,64}$/.test(normalized);
          }),
        acquisition_source: Yup.string()
          .trim()
          .max(120, t("clients.validation.acquisitionSourceMax")),
        notes: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(CLIENT_OPERATIONAL_NOTES_MAX_LENGTH, t("clients.validation.notesMax")),
      }),
    [hideCountryCode, t]
  );

  const formik = useFormik<QuickClientFormValues>({
    enableReinitialize: true,
    initialValues: emptyFormValues(),
    validationSchema,
    onSubmit: async (values, helpers) => {
      setApiError(null);

      const countryCode = hideCountryCode
        ? DEFAULT_COUNTRY_CODE
        : values.country_code.replace(/\D/g, "") || DEFAULT_COUNTRY_CODE;

      try {
        const secondaryDigits = values.secondary_phone_display.replace(/\D/g, "");
        const response = await createClient({
          name: values.name.trim(),
          country_code: countryCode,
          phone_number: values.phone_display.replace(/\D/g, ""),
          secondary_country_code: secondaryDigits ? countryCode : null,
          secondary_phone_number: secondaryDigits || null,
          gender: values.gender || null,
          instagram_handle: normalizeInstagram(values.instagram_handle),
          acquisition_source: toNullable(values.acquisition_source),
          notes: toNullable(values.notes),
        });

        helpers.resetForm({ values: emptyFormValues() });
        setMoreOpen(false);
        setApiError(null);
        onSuccess(response.data);
      } catch (error: any) {
        setApiError(error?.data || t("studio.clients.quick_create.error.generic"));
        helpers.setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm({ values: emptyFormValues() });
      setMoreOpen(false);
      setApiError(null);
    }
  }, [isOpen, formik]);

  const handleClose = () => {
    if (formik.isSubmitting) {
      return;
    }

    formik.resetForm({ values: emptyFormValues() });
    setMoreOpen(false);
    setApiError(null);
    onClose();
  };

  const phoneFieldInvalid = Boolean(
    formik.touched.phone_display && formik.errors.phone_display
  );
  const secondaryPhoneFieldInvalid = Boolean(
    formik.touched.secondary_phone_display && formik.errors.secondary_phone_display
  );
  const countryCodeFieldInvalid = Boolean(
    formik.touched.country_code && formik.errors.country_code
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {t("studio.clients.quick_create.title")}
      </ModalHeader>
      <ModalBody>
        {apiError ? (
          <Alert color="danger" className="border-0 bg-danger-subtle text-danger">
            {apiError}
          </Alert>
        ) : null}

        <Form
          id="quick-client-create-form"
          onSubmit={(event) => {
            event.preventDefault();
            formik.handleSubmit();
          }}
        >
          <div className="mb-3">
            <Label htmlFor="quick-client-name">
              {t("studio.clients.quick_create.fields.name.label")}
            </Label>
            <Input
              id="quick-client-name"
              name="name"
              autoFocus
              placeholder={t("studio.clients.quick_create.fields.name.placeholder")}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.name && formik.errors.name)}
            />
            {formik.touched.name && formik.errors.name ? (
              <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
            ) : null}
          </div>

          <div className="mb-3">
            <Label htmlFor="quick-client-notes">{t("clients.form.notesLabel")}</Label>
            <Input
              id="quick-client-notes"
              name="notes"
              type="textarea"
              rows={5}
              style={{ minHeight: 120 }}
              placeholder={t("clients.form.notesPlaceholder")}
              value={formik.values.notes ?? ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.notes && formik.errors.notes)}
              data-clarity-mask="true"
            />
            <FormText muted className="d-block mt-1">
              {t("clients.form.notesHint")}
            </FormText>
            {formik.touched.notes && formik.errors.notes ? (
              <FormFeedback type="invalid">{formik.errors.notes}</FormFeedback>
            ) : null}
          </div>

          {hideCountryCode ? (
            <Input type="hidden" name="country_code" value={DEFAULT_COUNTRY_CODE} readOnly />
          ) : null}

          <div className="mb-0">
            <Label htmlFor="quick-client-phone">
              {t("studio.clients.quick_create.fields.phone.label")}
            </Label>
            {hideCountryCode ? (
              <Cleave
                id="quick-client-phone"
                className={`form-control${phoneFieldInvalid ? " is-invalid" : ""}`}
                placeholder={t("studio.clients.quick_create.fields.phone.placeholder")}
                options={{
                  delimiters: ["(", ") ", "-"],
                  blocks: [0, 2, 5, 4],
                  numericOnly: true,
                }}
                value={formik.values.phone_display}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  formik.setFieldValue("phone_display", event.target.value);
                  if (apiError) {
                    setApiError(null);
                  }
                }}
                onBlur={() => formik.setFieldTouched("phone_display", true)}
              />
            ) : (
              <InputGroup>
                <InputGroupText>+</InputGroupText>
                <Input
                  id="quick-client-country-code"
                  name="country_code"
                  type="text"
                  inputMode="numeric"
                  placeholder={t("studio.clients.quick_create.fields.country_code.placeholder")}
                  value={formik.values.country_code}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
                    formik.setFieldValue("country_code", digits);
                    if (apiError) {
                      setApiError(null);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("country_code", true)}
                  invalid={countryCodeFieldInvalid}
                  style={{ maxWidth: 88 }}
                  aria-label={t("studio.clients.quick_create.fields.country_code.label")}
                />
                <Cleave
                  id="quick-client-phone"
                  className={`form-control${phoneFieldInvalid ? " is-invalid" : ""}`}
                  placeholder={t("studio.clients.quick_create.fields.phone.placeholder_international")}
                  options={{
                    numericOnly: true,
                  }}
                  value={formik.values.phone_display}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    formik.setFieldValue("phone_display", event.target.value);
                    if (apiError) {
                      setApiError(null);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("phone_display", true)}
                />
              </InputGroup>
            )}
            {formik.touched.country_code && formik.errors.country_code ? (
              <div className="invalid-feedback d-block">
                {formik.errors.country_code}
              </div>
            ) : null}
            {formik.touched.phone_display && formik.errors.phone_display ? (
              <div className="invalid-feedback d-block">{formik.errors.phone_display}</div>
            ) : null}
          </div>

          <div className="mb-0 mt-3">
            <Label htmlFor="quick-client-secondary-phone">
              {t("clients.fields.secondary_phone.label")}
            </Label>
            {hideCountryCode ? (
              <Cleave
                id="quick-client-secondary-phone"
                className={`form-control${secondaryPhoneFieldInvalid ? " is-invalid" : ""}`}
                placeholder={t("studio.clients.quick_create.fields.phone.placeholder")}
                options={{
                  delimiters: ["(", ") ", "-"],
                  blocks: [0, 2, 5, 4],
                  numericOnly: true,
                }}
                value={formik.values.secondary_phone_display}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  formik.setFieldValue("secondary_phone_display", event.target.value)
                }
                onBlur={() => formik.setFieldTouched("secondary_phone_display", true)}
              />
            ) : (
              <Cleave
                id="quick-client-secondary-phone"
                className={`form-control${secondaryPhoneFieldInvalid ? " is-invalid" : ""}`}
                placeholder={t(
                  "studio.clients.quick_create.fields.phone.placeholder_international"
                )}
                options={{
                  numericOnly: true,
                }}
                value={formik.values.secondary_phone_display}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  formik.setFieldValue("secondary_phone_display", event.target.value)
                }
                onBlur={() => formik.setFieldTouched("secondary_phone_display", true)}
              />
            )}
            {formik.touched.secondary_phone_display &&
            formik.errors.secondary_phone_display ? (
              <div className="invalid-feedback d-block">
                {formik.errors.secondary_phone_display}
              </div>
            ) : (
              <FormText muted className="d-block mt-1">
                {t("clients.fields.secondary_phone.hint")}
              </FormText>
            )}
          </div>

          <div className="mt-3">
            <Button
              color="link"
              type="button"
              className="btn-link text-decoration-none p-0 d-flex align-items-center gap-1 text-body"
              onClick={() => setMoreOpen((open) => !open)}
              aria-expanded={moreOpen}
            >
              <i
                className={`ri-arrow-${moreOpen ? "down" : "right"}-s-line fs-16`}
                aria-hidden
              />
              <span className="fs-13 fw-semibold">
                {t("studio.clients.quick_create.moreFields")}
              </span>
            </Button>
            {moreOpen ? (
              <div className="mt-2 d-flex flex-column gap-3">
                <div>
                  <Label htmlFor="quick-client-gender">
                    {t("clients.fields.gender.label")}
                  </Label>
                  <Input
                    id="quick-client-gender"
                    name="gender"
                    type="select"
                    value={formik.values.gender}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={!!(formik.touched.gender && formik.errors.gender)}
                  >
                    <option value="">{t("clients.fields.gender.empty")}</option>
                    {CLIENT_GENDERS.map((gender) => (
                      <option key={gender} value={gender}>
                        {t(`clients.fields.gender.${gender}`)}
                      </option>
                    ))}
                  </Input>
                  {formik.touched.gender && formik.errors.gender ? (
                    <FormFeedback type="invalid">{formik.errors.gender}</FormFeedback>
                  ) : null}
                </div>
                <div>
                  <Label htmlFor="quick-client-instagram">
                    {t("clients.fields.instagram.label")}
                  </Label>
                  <Input
                    id="quick-client-instagram"
                    name="instagram_handle"
                    placeholder={t("clients.fields.instagram.placeholder")}
                    value={formik.values.instagram_handle}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      !!(formik.touched.instagram_handle && formik.errors.instagram_handle)
                    }
                  />
                  {formik.touched.instagram_handle && formik.errors.instagram_handle ? (
                    <FormFeedback type="invalid">
                      {formik.errors.instagram_handle}
                    </FormFeedback>
                  ) : null}
                </div>
                <div>
                  <Label htmlFor="quick-client-acquisition">
                    {t("clients.fields.acquisition_source.label")}
                  </Label>
                  <Input
                    id="quick-client-acquisition"
                    name="acquisition_source"
                    placeholder={t("clients.fields.acquisition_source.placeholder")}
                    value={formik.values.acquisition_source}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      !!(
                        formik.touched.acquisition_source &&
                        formik.errors.acquisition_source
                      )
                    }
                  />
                  {formik.touched.acquisition_source &&
                  formik.errors.acquisition_source ? (
                    <FormFeedback type="invalid">
                      {formik.errors.acquisition_source}
                    </FormFeedback>
                  ) : (
                    <FormText muted className="d-block mt-1">
                      {t("clients.fields.acquisition_source.hint")}
                    </FormText>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleClose} disabled={formik.isSubmitting}>
          {t("studio.clients.quick_create.cancel")}
        </Button>
        <Button
          color="primary"
          type="submit"
          form="quick-client-create-form"
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t("studio.clients.quick_create.submitting")}
            </>
          ) : (
            t("studio.clients.quick_create.submit")
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default QuickClientCreateModal;
